import { IArea, SpriteRecord, SpriteType } from 'types/map';

import * as PIXI from 'pixi.js';
import { SELECTION_SPRITE_SUFFIXES, SPRITE_SHEET_FILE_NAMES } from 'components/Map/constants';
import GlobalSpriteMap from 'components/Map/PixiMap/GlobalSpriteMap';
import { AdjustmentFilter } from 'pixi-filters';
import * as SpriteCaching from './ReactLeafletPixiOverlay/sprite-caches';
import asStateAwareArea from 'mapping/asStateAwareArea';
import { parseImageName } from './util';
import { makeSpriteRecordComparator } from 'components/Map/util/makeSpriteRecordComparator';
import { updateSpriteForArea } from './ReactLeafletPixiOverlay/sprite-caches';


let haveWeLoaded = false;

const loadedSprites: { [areaKey: string]: boolean } = {};

export default async function preloadMapSprites(_areas: IArea[], onProgress?: (_: any) => void) {
  // We may call this many times over the app's lifetime, since it happens if the user changes their map
  // preference from compat -> non-compat, so we want to make sure that it's idempotent and the actual *loading* only
  // takes place once.
  if (haveWeLoaded) {
    return;
  }
  haveWeLoaded = true;

  // Begin by filtering out areas we've already loaded (this method may be called at any time)
  const areas = _areas.filter(a => !loadedSprites[a.areaKey]);
  const loader = PIXI.Loader.shared;

  // If we have an on progress callback to run, set it up now
  if (onProgress) {
    loader.onProgress.add(onProgress);
    // loader.onProgress = onProgress;
  }

  SPRITE_SHEET_FILE_NAMES.forEach(filename => loader.add(filename));

  let startAt = window.performance.now();
  await new Promise((resolve, /* reject */) => loader.load(resolve));
  let duration = window.performance.now() - startAt;
  console.info(`preloadMapSprites() took ${(Math.round(duration) / 1000).toFixed(2)} s`);

  startAt = window.performance.now();

  // Get [key, spritetype, sprite] tuples from spritesheet image file names
  const unsortedSpriteRecords: [string, SpriteType, PIXI.Sprite][] =
    SPRITE_SHEET_FILE_NAMES
      .map(filename => getAllSpritesFromSheet(loader.resources[filename]))
      .reduce((acc, next) => [...acc, ...next], []);

  // Add background sprites (which include stalagmites)
  unsortedSpriteRecords
    .filter(isForegroundSpriteRecord)
    .forEach(([_, aOrB, sprite]) => {
      SpriteCaching.addBackgroundSprite(sprite, aOrB as string);
    });

  // Order the sprites and exclude the background
  const orderedNonSelections = getSortedSpriteRecords(unsortedSpriteRecords, areas)
    .filter(sr =>
    !isBackgroundSpriteRecord(sr) &&
    !isForegroundSpriteRecord(sr) &&
    !isSelectionSpriteRecord(sr));


  console.info('Adding non selections.');
  const asynced = await orderedNonSelections.map(async ([areaKey, spriteType, sprite]) => {
    // Add the sprite to the cache, anyway, even if we don't know about it now
    await GlobalSpriteMap.put(areaKey, spriteType, sprite);

    const area = areas.find(a => a.areaKey === areaKey);
    // We may not have an area for this sprite, but if we do we should add it to the container now
    if (area) {
      console.info(`Adding '${spriteType}' for ${area.areaKey}`);
      await SpriteCaching.addAreaSpriteToContainer(area, spriteType);
      // await SpriteCaching.updateSpriteForArea(asStateAwareArea(area, areas, undefined));
    }
  });
  await Promise.all(asynced);

  // Add foreground sprites (which need to go underneath selection sprites)
  unsortedSpriteRecords
    .filter(isForegroundSpriteRecord)
    .forEach(([_, aOrB, sprite]) => {
      SpriteCaching.addForegroundSprite(sprite, aOrB as string);
    });

  // Order the selection sprites
  const orderedSelections = getSortedSpriteRecords(unsortedSpriteRecords, areas)
    .filter(sr =>
      !isBackgroundSpriteRecord(sr)
      && !isForegroundSpriteRecord(sr)
      && isSelectionSpriteRecord(sr)
    );

  const selectionsAdded = await orderedSelections.map(async ([areaKey, spriteType, sprite]) => {
    // Add the sprite to the cache, anyway, even if we don't know about it now
    await GlobalSpriteMap.put(areaKey, spriteType, sprite);

    const area = areas.find(a => a.areaKey === areaKey);
    // We may not have an area for this sprite, but if we do we should add it to the container now
    if (area) {
      console.info(`Adding ${spriteType} for ${area.areaKey}`);
      await SpriteCaching.addAreaSpriteToContainer(area, spriteType);
      // await SpriteCaching.updateSpriteForArea(asStateAwareArea(area, areas, undefined));
    }
  });
  await Promise.all(selectionsAdded);

  areas.forEach(area => updateSpriteForArea(asStateAwareArea(area, areas, undefined)));

  duration = (window.performance.now() - startAt) / 1000;
  console.info(`Adding sprites to container took ${duration.toFixed(2)} s`);

  SpriteCaching.forceRender();
}

function getAllSpritesFromSheet(sheet: PIXI.LoaderResource): [string, SpriteType, PIXI.Sprite][] {
  return Object.keys(sheet.textures!).map((imageName: string) => [
    parseImageName(imageName).areaKey,
    parseImageName(imageName).spriteType,
    getFilteredSpriteFromSpritesheet(sheet, imageName),
  ]);
}

function getFilteredSpriteFromSpritesheet(sheet: PIXI.LoaderResource, imageName: string): PIXI.Sprite {
  const { spriteType } = parseImageName(imageName);
  const sprite = new PIXI.Sprite(sheet.textures![imageName]);
  sprite.filters = createFiltersForSprite(spriteType);
  return sprite;
}

function createFiltersForSprite(whatKind: SpriteType) {
  const adj = new AdjustmentFilter({
    // gamma: 2,
    brightness: 0.35,
    saturation: 0.0,
    blue: 1.00,
    green: 1.05,
    red: 0.9,
  });
  const filters = [adj, new PIXI.filters.AlphaFilter(0)];
  filters[0].enabled = false; // Lit by default
  filters[1].enabled = whatKind === 'selection'; // Visible by default unless it's a selection glow
  return filters;
}

function getSortedSpriteRecords(unsortedSpriteRecords: SpriteRecord[], areas: IArea[]) {
  // Sort sprite records, putting districts lower in the stack
  const spriteRecords = [...unsortedSpriteRecords].sort(makeSpriteRecordComparator(areas));

  // We need to load and render non-selections first, so that selections sit on top
  const notSelections = spriteRecords.filter(sr => SELECTION_SPRITE_SUFFIXES.indexOf(sr[1]) < 0);
  const selections = spriteRecords.filter(sr => SELECTION_SPRITE_SUFFIXES.indexOf(sr[1]) >= 0);

  // Order selections so that non-selections are lower in the stack than selections
  return [...notSelections, ...selections];
}

function isBackgroundSpriteRecord(sr: SpriteRecord) {
  return sr[0] === 'background';
}

function isForegroundSpriteRecord(sr: SpriteRecord) {
  return sr[0] === 'foreground';
}

function isSelectionSpriteRecord(sr: SpriteRecord) {
  return sr[1] === 'selection' || sr[1] === 'main-destination-selection';
}